import React from 'react';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { useSelector, useDispatch } from 'react-redux';
import {
  WorksheetGridFieldSection,
  IWithWorksheetInjectedProps,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { Field, Checkbox } from 'Components/Core';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import deepEquals from 'Lib/Object/deepEquals';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { prefixRecordFieldSetNames } from 'Components/Worksheets/Core/WorksheetRecordGrid/helpers';
import { getGridFieldSet as getMoistureContentGridFieldSet } from 'Components/Worksheets/MoistureContentWorksheet/Astm/gridFieldSets';
import { ChangeEventWithData } from 'Components/Core/types';
import { joinPaths } from 'Lib/Object/Path/shared';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';

/**
 * Trials can only be added/removed/excluded in groups of 2
 */
const trialGroupSize = 2;

let AtterbergLimitPlasticLimitTrialSection: React.FC<IWithWorksheetInjectedProps> = ({
  namePrefix,
  createWorksheetSelector,
}) => {
  const dispatch = useDispatch();

  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));

  const preventTrialCreation = useSelector(
    pipe(
      createWorksheetSelector!('Trials'),
      valueOrDefault([] as Array<any>),
      (trials) => trials.filter((t) => !t.ExcludedTrial).length >= 2
    ),
    deepEquals
  );

  const trialRecordProps = useSelector(
    pipe(createWorksheetSelector!('Trials'), valueOrDefault([] as Array<any>), (trials) =>
      trials.map((t, i) => ({
        dim: t.ExcludedTrial,
        preventRemove: i < 2,
      }))
    ),
    deepEquals
  );

  const excludeTrials = (i: number, exclude: boolean) => {
    [...Array(trialGroupSize).keys()].forEach((k) =>
      dispatch(
        actions.setValue({
          name: joinPaths(namePrefix, 'Trials', `${i - k}`, 'ExcludedTrial'),
          value: exclude,
          sync: k === trialGroupSize - 1,
        })
      )
    );
  };

  return (
    <WorksheetGridFieldSection name="Trials">
      <WorksheetRecordGrid
        name={'Trials'}
        orientation={'horizontal'}
        recordProps={{
          width: 130,
          preventCreate: preventTrialCreation,
          preventRemove: (i) => trialRecordProps[i]?.preventRemove,
          dim: (i) => trialRecordProps[i]?.dim,
          removeConfirm: {
            confirm: true,
            confirmTitle: 'Trial Deletion',
            confirmText: 'Are you sure you want to delete this pair of trials?',
            confirmButtonText: 'Delete Trial',
          },
          recordGroupingSize: trialGroupSize,
        }}
        fieldSet={[
          ...prefixRecordFieldSetNames('MoistureDetermination', getMoistureContentGridFieldSet(dryMassEntryOption)),
          {
            name: 'ExcludedTrial',
            component: (i: number) =>
              i % trialGroupSize !== 0 ? (
                <Field input={<Checkbox />} onChange={(e: ChangeEventWithData) => excludeTrials(i, e?.data?.value)} />
              ) : (
                <></>
              ),
            label: 'Exclude Trial', // Remove when labels migrated to config
          },
        ]}
      />
    </WorksheetGridFieldSection>
  );
};

AtterbergLimitPlasticLimitTrialSection = withInjectedProps(AtterbergLimitPlasticLimitTrialSection);

export { AtterbergLimitPlasticLimitTrialSection as AtterbergLimitTrialSection };
export default AtterbergLimitPlasticLimitTrialSection;
